import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    SalaryDeductionCreateData,
    SalaryDeductionRequest,
    SalaryDeductionResponse,
    SalaryDeductionSliceModel,
    SalaryDeductionTableResponse,
    SalaryDeductionsListResponse,
} from "./salary-deduction-interfaces";
import { ErrorResponse, MainResponse, RejectedValue } from "shared/interfaces/core-interfaces";
import { AuthHeader } from "services/authHeader";
import { showNotification } from "shared/slices/statusNotificationsSlice";
import { NotificationTypes } from "shared/interfaces/status-notifications-interfaces";
import { API_BASE_URL as baseUri } from "config/env";

export const GetAllSalaryDeductions = createAsyncThunk<SalaryDeductionTableResponse[], void, { rejectValue: RejectedValue }>(
    'Slice/SalaryDeductionSlice/GetAllSalaryDeductions',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/salaryDeduction/all', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as SalaryDeductionsListResponse;
            return responseModel.salaryDeductions;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetMySalaryDeductions = createAsyncThunk<SalaryDeductionTableResponse[], void, { rejectValue: RejectedValue }>(
    'Slice/SalaryDeductionSlice/GetMySalaryDeductions',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/salaryDeduction/getByEmployee', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as SalaryDeductionsListResponse;
            return responseModel.salaryDeductions;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetSalaryDeduction = createAsyncThunk<SalaryDeductionResponse, string, { rejectValue: RejectedValue }>(
    'Slice/SalaryDeductionSlice/GetSalaryDeduction',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/salaryDeduction/get?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as SalaryDeductionResponse;
            return responseModel;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetAllInactiveSalaryDeductions = createAsyncThunk<SalaryDeductionTableResponse[], void, { rejectValue: RejectedValue }>(
    'Slice/SalaryDeductionSlice/GetAllInactiveSalaryDeductions',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/salaryDeduction/allInactive', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as SalaryDeductionsListResponse;
            return responseModel.salaryDeductions;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DeactivateSalaryDeduction = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/SalaryDeductionSlice/DeactivateSalaryDeduction',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/salaryDeduction/deactivate?id=' + id, {
            method: "PUT",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            dispatch(GetAllSalaryDeductions());
            dispatch(GetMySalaryDeductions());
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: responseModel.message ?? "Salary deduction deactivated successfully.", type: NotificationTypes.Success }));
            return responseModel;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetSalaryDeductionCreateData = createAsyncThunk<SalaryDeductionCreateData, void, { rejectValue: RejectedValue }>(
    'Slice/SalaryDeductionSlice/GetSalaryDeductionCreateData',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/salaryDeduction/getcreatedata', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            return await response.json() as SalaryDeductionCreateData;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateSalaryDeduction = createAsyncThunk<MainResponse, SalaryDeductionRequest, { rejectValue: RejectedValue }>(
    'Slice/SalaryDeductionSlice/CreateSalaryDeduction',
    async (request, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/salaryDeduction/create', {
            method: "POST",
            headers: AuthHeader(true, true),
            body: JSON.stringify(request),
        });
        if (response.status === 200) {
            dispatch(GetAllSalaryDeductions());
            dispatch(GetMySalaryDeductions());
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: responseModel.message ?? "Salary deduction created successfully.", type: NotificationTypes.Success }));
            return responseModel;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateSalaryDeduction = createAsyncThunk<MainResponse, SalaryDeductionRequest, { rejectValue: RejectedValue }>(
    'Slice/SalaryDeductionSlice/UpdateSalaryDeduction',
    async (request, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/salaryDeduction/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request),
        });
        if (response.status === 200) {
            dispatch(GetAllSalaryDeductions());
            dispatch(GetMySalaryDeductions());
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: responseModel.message ?? "Salary deduction updated successfully.", type: NotificationTypes.Success }));
            return responseModel;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeleteSalaryDeduction = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/SalaryDeductionSlice/DeleteSalaryDeduction',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/salaryDeduction/delete?id=' + id, {
            method: "DELETE",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            dispatch(GetAllSalaryDeductions());
            dispatch(GetMySalaryDeductions());
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: responseModel.message ?? "Salary deduction deleted successfully.", type: NotificationTypes.Success }));
            return responseModel;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

const initialValue: SalaryDeductionSliceModel = {
    statusCode: '',
    isLoading: false,
    isModalLoading: false,
    message: '',
    showNotification: false,
    listOfAllSalaryDeductions: [],
    listOfMySalaryDeductions: [],
    listOfAllInactiveSalaryDeductions: [],
    reloadData: false,
    salaryDeduction: null,
    createData: null,
    permissions: {
        createEditTenants: [],
        reactivateDeactivateTenants: [],
        deleteTenants: [],
    },
};

export const SalaryDeductionSlice = createSlice({
    name: 'SalaryDeductionSlice',
    initialState: initialValue,
    reducers: {
        resetSalaryDeductionSlice: (state) => {
            state.statusCode = '';
            state.isLoading = false;
            state.isModalLoading = false;
            state.reloadData = false;
            state.showNotification = false;
            state.message = '';
            state.listOfAllSalaryDeductions = [];
            state.listOfMySalaryDeductions = [];
            state.listOfAllInactiveSalaryDeductions = [];
            state.salaryDeduction = null;
            state.createData = null;
        },
        resetSalaryDeduction: (state) => {
            state.salaryDeduction = null;
            state.isModalLoading = false;
            state.reloadData = false;
        },
        resetStatusCode: (state) => {
            state.statusCode = '';
            state.message = '';
            state.showNotification = false;
            state.reloadData = false;
        },
    },
    extraReducers(builder) {
        // GetAllSalaryDeductions
        builder
            .addCase(GetAllSalaryDeductions.fulfilled, (state, action: PayloadAction<SalaryDeductionTableResponse[]>) => {
                state.isLoading = false;
                state.message = '';
                state.listOfAllSalaryDeductions = action.payload;
            })
            .addCase(GetAllSalaryDeductions.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetAllSalaryDeductions.pending, state => {
                state.isLoading = true;
            });
        // GetMySalaryDeductions
        builder
            .addCase(GetMySalaryDeductions.fulfilled, (state, action: PayloadAction<SalaryDeductionTableResponse[]>) => {
                state.isLoading = false;
                state.listOfMySalaryDeductions = action.payload;
            })
            .addCase(GetMySalaryDeductions.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetMySalaryDeductions.pending, state => {
                state.isLoading = true;
            });
        // GetSalaryDeduction
        builder
            .addCase(GetSalaryDeduction.fulfilled, (state, action: PayloadAction<SalaryDeductionResponse>) => {
                state.isModalLoading = false;
                state.message = '';
                state.salaryDeduction = action.payload;
            })
            .addCase(GetSalaryDeduction.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetSalaryDeduction.pending, state => {
                state.isModalLoading = true;
            });
        // GetSalaryDeductionCreateData
        builder
            .addCase(GetSalaryDeductionCreateData.fulfilled, (state, action: PayloadAction<SalaryDeductionCreateData>) => {
                state.isModalLoading = false;
                state.createData = action.payload;
            })
            .addCase(GetSalaryDeductionCreateData.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetSalaryDeductionCreateData.pending, state => {
                state.isModalLoading = true;
            });
        // CreateSalaryDeduction
        builder
            .addCase(CreateSalaryDeduction.fulfilled, (state) => {
                state.isModalLoading = false;
                state.reloadData = true;
            })
            .addCase(CreateSalaryDeduction.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateSalaryDeduction.pending, state => {
                state.isModalLoading = true;
            });
        // UpdateSalaryDeduction
        builder
            .addCase(UpdateSalaryDeduction.fulfilled, (state, action) => {
                const req = action.meta.arg;
                if (req.id) {
                    const typeName = state.createData?.type.find(t => t.value === req.type)?.label;
                    const paymentIntervalName = state.createData?.paymentInterval.find(p => p.value === req.paymentInterval)?.label;
                    const update = (r: SalaryDeductionTableResponse) =>
                        r.id === req.id
                            ? {
                                ...r,
                                type: req.type,
                                typeName: typeName ?? r.typeName,
                                paymentInterval: req.paymentInterval,
                                paymentIntervalName: paymentIntervalName ?? r.paymentIntervalName,
                                from: req.from as Date,
                                to: req.to as Date,
                                amount: req.amount,
                                currency: req.currency,
                                comment: req.comment
                            }
                            : r;
                    state.listOfAllSalaryDeductions = state.listOfAllSalaryDeductions.map(update);
                    state.listOfMySalaryDeductions = state.listOfMySalaryDeductions.map(update);
                }

                state.isModalLoading = false;
                state.reloadData = true;
            })
            .addCase(UpdateSalaryDeduction.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateSalaryDeduction.pending, state => {
                state.isModalLoading = true;
            });
        // GetAllInactiveSalaryDeductions
        builder
            .addCase(GetAllInactiveSalaryDeductions.fulfilled, (state, action: PayloadAction<SalaryDeductionTableResponse[]>) => {
                state.isLoading = false;
                state.listOfAllInactiveSalaryDeductions = action.payload;
            })
            .addCase(GetAllInactiveSalaryDeductions.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetAllInactiveSalaryDeductions.pending, state => {
                state.isLoading = true;
            });
        // DeactivateSalaryDeduction
        builder
            .addCase(DeactivateSalaryDeduction.fulfilled, (state, action) => {
                const id = action.meta.arg;
                const update = (r: SalaryDeductionTableResponse) =>
                    r.id === id ? { ...r, status: 'Canceled' } : r;
                state.listOfAllSalaryDeductions = state.listOfAllSalaryDeductions.map(update);
                state.listOfMySalaryDeductions = state.listOfMySalaryDeductions.map(update);

                state.isLoading = false;
                state.reloadData = true;
            })
            .addCase(DeactivateSalaryDeduction.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeactivateSalaryDeduction.pending, state => {
                state.isLoading = true;
            });
        // DeleteSalaryDeduction
        builder
            .addCase(DeleteSalaryDeduction.fulfilled, (state) => {
                state.isLoading = false;
                state.reloadData = true;
            })
            .addCase(DeleteSalaryDeduction.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteSalaryDeduction.pending, state => {
                state.isLoading = true;
            });
    },
});

export const { resetSalaryDeduction, resetSalaryDeductionSlice, resetStatusCode } = SalaryDeductionSlice.actions;
export default SalaryDeductionSlice.reducer;
